import type { UserInfo } from '../auth/TokenManager';
import type { JournalPermissions, JournalTemplate } from '../types/journal';
import type { ApiResponse } from '../utils/ApiService';

/** What the Journal tab shows for one user: their rights, and the templates if Journal is on. */
export interface JournalTabData {
  user: UserInfo;
  perms: JournalPermissions;
  enabled: boolean;
  templates: JournalTemplate[];
  emptyHint: string;
}

/** Where the Journal tab's data comes from, all read with the same headers. */
export interface JournalTabSources {
  identity(): Promise<UserInfo>;
  permissions(): Promise<ApiResponse<JournalPermissions>>;
  /** Throws when the list could not be read. */
  templates(): Promise<JournalTemplate[]>;
  isAdmin(): Promise<boolean>;
}

/**
 * Reads what the Journal tab shows. Throws when the rights or the templates could not be read: shown, a
 * failed read would say Journal is turned off, or that the company has no templates.
 */
export async function readJournalTabData(sources: JournalTabSources): Promise<JournalTabData> {
  const user = await sources.identity();
  const response = await sources.permissions();
  if (!response.success || !response.data) throw new Error(response.error || 'Failed to read the Journal rights');
  const perms = response.data;
  const enabled = perms.companyJournalEnabled === true;
  const templates = enabled ? (await sources.templates()).filter(t => t.status === 'ACTIVE') : [];
  const emptyHint = templates.length > 0 ? '' : await sources.isAdmin()
    ? 'No active templates. Create one in the web portal under Journal.'
    : 'No active templates. Ask your admin to create one in the web portal.';
  return { user, perms, enabled, templates, emptyHint };
}
